import { apiFetch } from '@/lib/api';

export interface User {
  id: number;
  tenant_id: number;
  email: string;
  name: string;
  role: 'admin' | 'member';
  is_active: boolean;
  email_verified_at: string | null;
  created_at: string;
}

export interface SignupRequest {
  business_name: string;
  owner_name: string;
  email: string;
  password: string;
  language: 'pt-BR' | 'pt-PT';
  // F030 Layer 3 — Cloudflare Turnstile token. Omitted when the site key env
  // var is empty (dev convenience); the backend fails closed when enabled.
  turnstile_token?: string;
  // F132 — acquisition attribution (3-layer: promo code, ?ref= link, self-report).
  // `ref` is captured invisibly from the signup URL; `acquisition_source` +
  // `acquisition_note` come from the optional "como nos conheceu" form field.
  ref?: string;
  acquisition_source?: 'instagram' | 'creator' | 'google' | 'community' | 'other';
  acquisition_note?: string;
}

export interface SignupResponse {
  tenant_id: number;
  user_id: number;
  message: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: 'Bearer';
  user: User;
  email_verification_required: boolean;
}

export interface MessageResponse {
  message: string;
}

export const authService = {
  signup(payload: SignupRequest): Promise<SignupResponse> {
    return apiFetch<SignupResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  },

  login(payload: LoginRequest): Promise<LoginResponse> {
    return apiFetch<LoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  },

  getMe(): Promise<User> {
    return apiFetch<User>('/auth/me');
  },

  requestPasswordReset(email: string): Promise<MessageResponse> {
    return apiFetch<MessageResponse>('/auth/password-reset-request', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  },

  confirmPasswordReset(token: string, new_password: string): Promise<MessageResponse> {
    return apiFetch<MessageResponse>('/auth/password-reset-confirm', {
      method: 'POST',
      body: JSON.stringify({ token, new_password }),
      skipAuth: true,
    });
  },

  resendVerification(email: string): Promise<MessageResponse> {
    return apiFetch<MessageResponse>('/auth/email-verification-resend', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  },

  confirmVerification(token: string): Promise<MessageResponse> {
    return apiFetch<MessageResponse>('/auth/email-verification-confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
      skipAuth: true,
    });
  },
};
